import React from 'react'
import { Route, Switch } from 'react-router-dom'

import { NotFoundRoute, WonderschoolRouter } from 'framework'
import Loader from 'main/components/common/Loader'
import { App, AuthenticatedRoute } from 'main/framework'
import { Flags, useFeatureFlag } from 'main/hooks/useFeatureFlag'
import WSA from 'sdk/sso'

import Complete from './pages/Envelope/Complete'
import StaffDownloadApp from './pages/StaffDownloadApp'
import StaffSignUp from './pages/StaffSignUp'

const Application = React.lazy(() => import('main/pages/Application'))
const Locations = React.lazy(() => import('main/pages/Locations'))
const ExternalSchool = React.lazy(() => import('main/pages/ExternalSchool'))
const Design = React.lazy(() => import('main/pages/Design'))
const Family = React.lazy(() => import('main/pages/Family'))
const Join = React.lazy(() => import('main/pages/Join'))
const JoinSSO = React.lazy(() => import('main/pages/Join/JoinSSO'))
const Login = React.lazy(() => import('main/pages/Login'))
const LoginSSO = React.lazy(() => import('main/pages/Login/LoginSSO'))
const AuthReturnSSO = React.lazy(() => import('main/pages/AuthReturnSSO'))
const LogoutCompleteSSO = React.lazy(() => import('main/pages/LogoutCompleteSSO'))
const Me = React.lazy(() => import('main/pages/Me'))
const PaymentMethods = React.lazy(() => import('main/pages/PaymentMethods'))
const MobileApp = React.lazy(() => import('main/pages/MobileApp'))
const WSApp = React.lazy(() => import('main/pages/WSApp'))
const Chat = React.lazy(() => import('main/pages/Chat'))
const Onboarding = React.lazy(() => import('main/pages/Onboarding'))
const OnboardingV1 = React.lazy(() => import('main/pages/OnboardingBeta'))
const Region = React.lazy(() => import('main/pages/Region'))
const School = React.lazy(() => import('main/pages/School'))
const Search = React.lazy(() => import('main/pages/Search'))
const Dashboard = React.lazy(() => import('main/pages/Dashboard/Dashboard'))
const Invitation = React.lazy(() => import('main/pages/Invitations'))
const Home = React.lazy(() => import('main/pages/Home'))
const HomeSSO = React.lazy(() => import('main/pages/Home/HomeSSO'))
const FeatureFlags = React.lazy(() => import('main/pages/FeatureFlags'))
const Password = React.lazy(() => import('main/pages/Me/pages/Password'))
const SavedSearches = React.lazy(() => import('main/pages/Me/pages/SavedSearches'))

function Router() {
  const ssoFlag = useFeatureFlag(Flags.SSO)
  const isSSO = ssoFlag.enabled()

  const LoginPage = isSSO ? LoginSSO : Login
  const JoinPage = isSSO ? JoinSSO : Join
  const HomePage = isSSO ? HomeSSO : Home

  return (
    <React.Suspense fallback={<Loader />}>
      <App>
        <WonderschoolRouter>
          <Switch>
            <Route exact path="/" component={HomePage} />
            <Route exact path="/dashboard" component={HomePage} />
            <Route exact path="/home" component={Home} />
            <Route exact path="/feature-flags" component={FeatureFlags} />
            <Route exact path="/staff-accept/download-app" component={StaffDownloadApp} />
            <Route exact path="/staff-accept/:emailCode" component={StaffSignUp} />
            <Route exact path="/invitations/:step/:invitationId" component={Invitation} />
            <AuthenticatedRoute path="/admin/:schoolId(\d+)" component={Dashboard} />
            <Route exact path="/envelope/complete" component={Complete} />
            <AuthenticatedRoute path="/messages/:channelId" component={Chat} />
            <AuthenticatedRoute path="/messages" component={Chat} />
            <AuthenticatedRoute path="/application/:schoolId(\d+)" component={Application} />
            <AuthenticatedRoute path="/instant-enroll/:schoolId(\d+)" component={Application} />
            <AuthenticatedRoute exact path="/design" component={Design} />
            <AuthenticatedRoute path="/family" component={Family} />
            <Route exact path="/locations" component={Locations} />
            <Route exact path="/login" component={LoginPage} />
            <Route exact path="/join" component={JoinPage} />
            <Route exact path="/join/:networkId" component={JoinPage} />
            <Route exact path="/auth-return" component={AuthReturnSSO} />
            <Route exact path="/logout-complete" component={LogoutCompleteSSO} />
            <AuthenticatedRoute
              adminRequired
              path="/original/onboarding/v1/:task/:step"
              component={OnboardingV1}
            />
            <AuthenticatedRoute path="/original/onboarding/:task/:step" component={Onboarding} />

            <AuthenticatedRoute path="/me/password" component={Password} />
            <AuthenticatedRoute path="/me/saved-searches" component={SavedSearches} />
            <Route exact path="/search/:query?" component={Search} />
            <Route exact path="/state/:state/city/:city" component={Region} />
            <Route exact path="/state/:state" component={Region} />
            <Route exact path="/:state/:city" component={Region} />
            <Route
              exact
              path="/:state/:city/*-:externalSchoolId(\d+)u"
              component={ExternalSchool}
            />
            <Route
              exact
              path="listings-legacy/:state/:city/*-:externalSchoolId(\d+)u"
              component={ExternalSchool}
            />
            <Route exact path="/:state/:city/*-:schoolId(\d+)" component={School} />
            <Route exact path="/*-:externalSchoolId(\d+)u" component={ExternalSchool} />
            <Route exact path="/*-:schoolId(\d+)" component={School} />
            <Route exact path="/listings-legacy/:state/:city/*-:schoolId(\d+)" component={School} />

            <AuthenticatedRoute path="/me" component={Me} />
            <Route exact path="/mobile-app" component={MobileApp} />
            <Route exact path="/wsapp" component={WSApp} />
            <Route exact path="/wsapp/*" component={WSApp} />
            <AuthenticatedRoute exact path="/payment_methods" component={PaymentMethods} />
            <NotFoundRoute />
          </Switch>
        </WonderschoolRouter>
      </App>
    </React.Suspense>
  )
}

export default React.memo(WSA.Components.withWSAuth(Router))
